import { useState, useEffect } from 'react';

interface Props {
  slug: string;
  color: string;
}

type Vote = 'like' | 'dislike' | null;

const STORAGE_KEY = (slug: string) => `article_vote_${slug}`;

// Realistic mock base counts per article
const getBaseCounts = (slug: string) => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) & 0xffff;
  return {
    likes: 80 + (hash % 120),
    dislikes: 3 + (hash % 12),
  };
};

const ArticleLikeWidget = ({ slug, color }: Props) => {
  const base = getBaseCounts(slug);
  const [vote, setVote] = useState<Vote>(null);
  const [counts, setCounts] = useState({ likes: base.likes, dislikes: base.dislikes });
  const [animating, setAnimating] = useState<Vote>(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY(slug)) as Vote;
    if (saved) {
      setVote(saved);
      setCounts(prev => ({
        likes: saved === 'like' ? prev.likes + 1 : prev.likes,
        dislikes: saved === 'dislike' ? prev.dislikes + 1 : prev.dislikes,
      }));
    }
  }, [slug]);

  const handleVote = (type: 'like' | 'dislike') => {
    if (vote === type) {
      // Undo vote
      setVote(null);
      localStorage.removeItem(STORAGE_KEY(slug));
      setCounts(prev => ({
        likes: type === 'like' ? prev.likes - 1 : prev.likes,
        dislikes: type === 'dislike' ? prev.dislikes - 1 : prev.dislikes,
      }));
      return;
    }

    const prev = vote;
    setVote(type);
    localStorage.setItem(STORAGE_KEY(slug), type);
    setAnimating(type);
    setTimeout(() => setAnimating(null), 500);

    setCounts(c => ({
      likes: type === 'like'
        ? c.likes + 1
        : prev === 'like' ? c.likes - 1 : c.likes,
      dislikes: type === 'dislike'
        ? c.dislikes + 1
        : prev === 'dislike' ? c.dislikes - 1 : c.dislikes,
    }));
  };

  const total = counts.likes + counts.dislikes;
  const likePercent = total > 0 ? Math.round((counts.likes / total) * 100) : 0;

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div
          className="w-1 h-5 rounded-full"
          style={{ background: `linear-gradient(to bottom, #39ff14, transparent)` }}
        />
        <span className="font-orbitron font-bold text-xs text-white">Оценить статью</span>
      </div>

      {/* Question */}
      <p className="font-mono text-[11px] text-white/40 mb-4 leading-relaxed">
        Был ли материал полезным для вас?
      </p>

      {/* Buttons */}
      <div className="flex gap-3 mb-4">
        {/* Like */}
        <button
          onClick={() => handleVote('like')}
          className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden"
          style={{
            background: vote === 'like' ? 'rgba(57,255,20,0.12)' : 'rgba(255,255,255,0.03)',
            border: vote === 'like' ? '1px solid rgba(57,255,20,0.35)' : '1px solid rgba(255,255,255,0.08)',
            transform: animating === 'like' ? 'scale(0.94)' : 'scale(1)',
          }}
          aria-label="Полезная статья"
          aria-pressed={vote === 'like'}
        >
          {vote === 'like' && (
            <div
              className="absolute inset-0 rounded-xl pointer-events-none"
              style={{ background: 'radial-gradient(circle at 50% 50%, rgba(57,255,20,0.15) 0%, transparent 70%)' }}
            />
          )}
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200"
            style={{
              background: vote === 'like' ? 'rgba(57,255,20,0.18)' : 'rgba(255,255,255,0.05)',
            }}
          >
            <i
              className={vote === 'like' ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}
              style={{
                fontSize: '16px',
                color: vote === 'like' ? '#39ff14' : 'rgba(255,255,255,0.35)',
                filter: vote === 'like' ? 'drop-shadow(0 0 6px #39ff14)' : 'none',
                transition: 'all 0.2s ease',
              }}
            />
          </div>
          <span
            className="font-orbitron font-bold text-sm transition-all duration-200"
            style={{ color: vote === 'like' ? '#39ff14' : 'rgba(255,255,255,0.5)' }}
          >
            {counts.likes}
          </span>
          <span
            className="font-mono text-[9px] transition-colors duration-200"
            style={{ color: vote === 'like' ? 'rgba(57,255,20,0.7)' : 'rgba(255,255,255,0.2)' }}
          >
            Полезно
          </span>
        </button>

        {/* Dislike */}
        <button
          onClick={() => handleVote('dislike')}
          className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden"
          style={{
            background: vote === 'dislike' ? 'rgba(255,45,120,0.1)' : 'rgba(255,255,255,0.03)',
            border: vote === 'dislike' ? '1px solid rgba(255,45,120,0.3)' : '1px solid rgba(255,255,255,0.08)',
            transform: animating === 'dislike' ? 'scale(0.94)' : 'scale(1)',
          }}
          aria-label="Бесполезная статья"
          aria-pressed={vote === 'dislike'}
        >
          {vote === 'dislike' && (
            <div
              className="absolute inset-0 rounded-xl pointer-events-none"
              style={{ background: 'radial-gradient(circle at 50% 50%, rgba(255,45,120,0.12) 0%, transparent 70%)' }}
            />
          )}
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200"
            style={{
              background: vote === 'dislike' ? 'rgba(255,45,120,0.15)' : 'rgba(255,255,255,0.05)',
            }}
          >
            <i
              className={vote === 'dislike' ? 'ri-thumb-down-fill' : 'ri-thumb-down-line'}
              style={{
                fontSize: '16px',
                color: vote === 'dislike' ? '#ff2d78' : 'rgba(255,255,255,0.35)',
                filter: vote === 'dislike' ? 'drop-shadow(0 0 6px #ff2d78)' : 'none',
                transition: 'all 0.2s ease',
              }}
            />
          </div>
          <span
            className="font-orbitron font-bold text-sm transition-all duration-200"
            style={{ color: vote === 'dislike' ? '#ff2d78' : 'rgba(255,255,255,0.5)' }}
          >
            {counts.dislikes}
          </span>
          <span
            className="font-mono text-[9px] transition-colors duration-200"
            style={{ color: vote === 'dislike' ? 'rgba(255,45,120,0.7)' : 'rgba(255,255,255,0.2)' }}
          >
            Не очень
          </span>
        </button>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <span className="font-mono text-[9px] text-white/25">Рейтинг полезности</span>
          <span
            className="font-orbitron font-bold text-[10px]"
            style={{ color: likePercent >= 70 ? '#39ff14' : likePercent >= 40 ? color : '#ff2d78' }}
          >
            {likePercent}%
          </span>
        </div>
        <div
          className="w-full h-1.5 rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.06)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${likePercent}%`,
              background: likePercent >= 70
                ? 'linear-gradient(90deg, #39ff14, #00f5ff)'
                : likePercent >= 40
                  ? `linear-gradient(90deg, ${color}, #f0ff00)`
                  : 'linear-gradient(90deg, #ff2d78, #ff6b00)',
              boxShadow: likePercent >= 70 ? '0 0 8px rgba(57,255,20,0.5)' : 'none',
            }}
          />
        </div>
        <p className="font-mono text-[9px] text-white/20 text-center">
          {total} {total === 1 ? 'оценка' : total >= 2 && total <= 4 ? 'оценки' : 'оценок'}
        </p>
      </div>

      {/* Thank you message */}
      {vote && (
        <div
          className="mt-3 py-2 px-3 rounded-lg text-center"
          style={{
            background: vote === 'like' ? 'rgba(57,255,20,0.06)' : 'rgba(255,45,120,0.06)',
            border: `1px solid ${vote === 'like' ? 'rgba(57,255,20,0.15)' : 'rgba(255,45,120,0.15)'}`,
          }}
        >
          <p
            className="font-mono text-[10px]"
            style={{ color: vote === 'like' ? 'rgba(57,255,20,0.8)' : 'rgba(255,45,120,0.8)' }}
          >
            {vote === 'like' ? 'Спасибо! Рады, что помогли' : 'Спасибо за честность'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ArticleLikeWidget;
